/**
 * Protocol List Page
 * Displays all trials with expandable sessions (matching Flutter layout)
 *
 * Uses modular-ui ProgressOverlay for loading.
 */

import React, {useState, useEffect, useRef, useCallback} from 'react';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  Button,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  Stack,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  UnfoldMore as ExpandAllIcon,
  UnfoldLess as CollapseAllIcon,
  Search as SearchIcon,
  EditNote as DraftIcon,
  Autorenew as InProgressIcon,
  CheckCircle as CompletedIcon,
  Settings as SettingsIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
  MenuBook as KBIcon,
} from '@mui/icons-material';
import {ProgressOverlay} from '@signanthealth/modular-ui';
import PlatformActionChip from '../components/PlatformActionChip';
import {useNavigate} from 'react-router-dom';
import {apiService} from '../services/api';
import {ManageProtocolDialog} from '../components/ManageProtocolDialog';
import {ManagePRSDialog} from '../components/ManagePRSDialog';
import {AddTrialDialog} from '../components/AddTrialDialog';
import {AddSessionDialog} from '../components/AddSessionDialog';
import {useAnalysisStatus} from '../hooks/useAnalysisStatus';
import {config} from '../config';

// ============================================
// TYPES
// ============================================

interface PrsVersionInfo {
  version: string;
  is_base: boolean;
  uploaded_at?: string;
}

interface PlatformInfo {
  platform: string;
  analysis_available: boolean;
}

interface SessionInfo {
  session_id: string;
  name: string;
  platforms: string[];
  created_at: string | null;
  status: string;
  source_prs_version?: string;
  target_prs_version?: string;
}

interface TrialView {
  trial_id: string;
  platforms: string[];
  platformInfos: PlatformInfo[]; // Includes analysis_available flag
  prs_count: number;
  prs_versions: PrsVersionInfo[];
  description?: string;
  jira_ticket?: string;
  sessions: SessionInfo[];
  app_zip_filename?: string | null;
  web_zip_filename?: string | null;
}

// ============================================
// HELPERS
// ============================================

/** Normalize platforms from API (can be string[] or {platform, analysis_available}[]) */
const normalizePlatforms = (platforms: any): string[] => {
  if (!platforms || !Array.isArray(platforms)) return [];
  if (platforms.length === 0) return [];
  if (typeof platforms[0] === 'string') return platforms;
  return platforms.map((p: any) => p.platform || p);
};

/** Extract platform info including analysis_available from API */
const normalizePlatformInfos = (platforms: any): PlatformInfo[] => {
  if (!platforms || !Array.isArray(platforms)) return [];
  if (platforms.length === 0) return [];
  if (typeof platforms[0] === 'string') {
    return platforms.map((p: string) => ({ platform: p, analysis_available: false }));
  }
  return platforms.map((p: any) => ({
    platform: p.platform || p,
    analysis_available: p.analysis_available || false,
  }));
};

/** Normalize PRS versions from API (can be string[] or {version, is_base}[]) */
const normalizePrsVersions = (prsVersions: any): PrsVersionInfo[] => {
  if (!prsVersions || !Array.isArray(prsVersions)) return [];
  if (prsVersions.length === 0) return [];
  if (typeof prsVersions[0] === 'string') {
    return prsVersions.map((v: string) => ({version: v, is_base: false}));
  }
  return prsVersions.map((v: any) => ({
    version: v.version || v,
    is_base: v.is_base || false,
    uploaded_at: v.uploaded_at,
  }));
};

// ============================================
// MAIN COMPONENT
// ============================================

const ProtocolList: React.FC = () => {
  const navigate = useNavigate();
  const [trials, setTrials] = useState<TrialView[]>([]);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(config.defaultPageSize);
  const [totalCount, setTotalCount] = useState(0);
  const [filterText, setFilterText] = useState('');
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Analysis status tracking (mirrors Flutter's AnalysisStatusCubit)
  const analysisStatus = useAnalysisStatus();

  // Upload Dialogs state
  const [uploadProtocolDialogOpen, setUploadProtocolDialogOpen] = useState(false);
  const [uploadPRSDialogOpen, setUploadPRSDialogOpen] = useState(false);
  const [addTrialDialogOpen, setAddTrialDialogOpen] = useState(false);
  const [addSessionDialogOpen, setAddSessionDialogOpen] = useState(false);
  const [selectedTrialForProtocol, setSelectedTrialForProtocol] = useState<TrialView | null>(null);
  const [selectedTrialForPRS, setSelectedTrialForPRS] = useState<{trialId: string; platform: 'app' | 'web'} | null>(null);
  const [selectedTrialForSession, setSelectedTrialForSession] = useState<TrialView | null>(null);
  /** Full trial detail for Add Session dialog (fetched on open so dialog has prs_versions with is_base) */
  const [sessionDialogTrialDetail, setSessionDialogTrialDetail] = useState<TrialView | null>(null);
  const [sessionDialogLoading, setSessionDialogLoading] = useState(false);

  // Analysis progress dialog
  const [analysisProgressOpen, setAnalysisProgressOpen] = useState(false);
  const [analysisProgressTrialId, setAnalysisProgressTrialId] = useState('');
  const [analysisProgressPlatform, setAnalysisProgressPlatform] = useState('');
  const [showFailedWorkerDetails, setShowFailedWorkerDetails] = useState(false);

  // Workflow mode pre-check dialog (mirrors Flutter's Clear Plans / Hard Block dialogs)
  const [workflowCheckLoadingKey, setWorkflowCheckLoadingKey] = useState<string | null>(null);
  const [workflowDialogOpen, setWorkflowDialogOpen] = useState(false);
  const [workflowDialogType, setWorkflowDialogType] = useState<'clear_plans' | 'hard_block'>('clear_plans');
  const [workflowDialogReason, setWorkflowDialogReason] = useState('');
  const [workflowDialogSummary, setWorkflowDialogSummary] = useState<{planned?: number; active?: number; committed?: number}>({});
  const [pendingWizardNav, setPendingWizardNav] = useState<{trialId: string; sessionId: string; version: string; platform: string; platforms: string[]} | null>(null);

  // Edit description state
  const [editDescriptionDialogOpen, setEditDescriptionDialogOpen] = useState(false);
  const [editingTrial, setEditingTrial] = useState<TrialView | null>(null);
  const [editedDescription, setEditedDescription] = useState('');
  const [savingDescription, setSavingDescription] = useState(false);

  // ============================================
  // DATA LOADING
  // ============================================

  /** Guard against concurrent/duplicate loads (React StrictMode double-mount) */
  const loadingRef = useRef(false);

  const loadTrials = async (overrides?: { page?: number; rowsPerPage?: number; filter?: string }) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    try {
      setLoading(true);
      setError(null);

      const effectivePage = overrides?.page ?? page;
      const effectiveRowsPerPage = overrides?.rowsPerPage ?? rowsPerPage;
      const effectiveFilterText = overrides?.filter !== undefined ? overrides.filter : filterText;
      const effectiveFilter = effectiveFilterText.trim()
        ? `trial_id:contains:${effectiveFilterText.trim()},description:contains:${effectiveFilterText.trim()}`
        : '';

      const response = await apiService.listTrials({
        limit: effectiveRowsPerPage,
        offset: effectivePage * effectiveRowsPerPage,
        filter: effectiveFilter || undefined,
      });

      if (!response.success) {
        throw new Error('Failed to load trials from API');
      }

      const trialsList: TrialView[] = response.trials.map((trial) => {
        const prs_versions = normalizePrsVersions(trial.prs_versions);
        const prs_count =
          typeof trial.prs_count === 'number' ? trial.prs_count : prs_versions.length;
        return {
        trial_id: trial.trial_id,
        platforms: normalizePlatforms(trial.platforms),
        platformInfos: normalizePlatformInfos(trial.platforms),
        prs_count,
        prs_versions,
        description: trial.description || undefined,
        jira_ticket: undefined,
        app_zip_filename: trial.app_zip_filename,
        web_zip_filename: trial.web_zip_filename,
        sessions: (trial.sessions || []).map((s) => ({
          session_id: s.session_id,
          name: s.name || s.session_id,
          platforms: s.platforms || [],
          created_at: s.created_at,
          status: s.status || 'draft',
          source_prs_version: s.source_prs_version,
          target_prs_version: s.target_prs_version,
        })),
      };
      });

      setTrials(trialsList);
      setTotalCount(response.total_count);
      setExpandedRows(new Set());

      // Check analysis status for each trial+platform (like Flutter's AnalysisStatusCubit)
      for (const trial of trialsList) {
        for (const platform of trial.platforms) {
          analysisStatus.checkStatus(trial.trial_id, platform);
        }
      }

      loadSessionsForTrials(trialsList);
    } catch (err: any) {
      setError(err.message || 'Failed to load trials');
      console.error('Error loading trials:', err);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  /** Map a single trial API response (getTrial or list item) to TrialView */
  const trialResponseToTrialView = useCallback((trial: any): TrialView => {
    const prs_versions = normalizePrsVersions(trial.prs_versions);
    const prs_count =
      typeof trial.prs_count === 'number' ? trial.prs_count : prs_versions.length;
    return {
    trial_id: trial.trial_id,
    platforms: normalizePlatforms(trial.platforms),
    platformInfos: normalizePlatformInfos(trial.platforms),
    prs_count,
    prs_versions,
    description: trial.description || undefined,
    jira_ticket: undefined,
    app_zip_filename: trial.app_zip_filename,
    web_zip_filename: trial.web_zip_filename,
    sessions: (trial.sessions || []).map((s: any) => ({
      session_id: s.session_id,
      name: s.name || s.session_id,
      platforms: s.platforms || [],
      created_at: s.created_at,
      status: s.status || 'draft',
      source_prs_version: s.source_prs_version,
      target_prs_version: s.target_prs_version,
    })),
  };
  }, []);

  /** Refresh a single trial in the list (e.g. after protocol replace) and update analysis status for that trial only */
  const refreshTrialAndStatus = useCallback(
    async (trialId: string, platform: 'app' | 'web') => {
      try {
        const response = await apiService.getTrial(trialId);
        if (!response.success) return;
        const updatedTrial = trialResponseToTrialView(response);
        setTrials((prev) =>
          prev.map((t) => (t.trial_id === trialId ? updatedTrial : t))
        );
        analysisStatus.setStatusLoading(trialId, platform);
        analysisStatus.checkStatus(trialId, platform);
      } catch (err) {
        console.error('Error refreshing trial after protocol replace:', err);
      }
    },
    [trialResponseToTrialView, analysisStatus]
  );

  /** Load sessions for each trial if not already included from list API */
  const loadSessionsForTrials = async (trialsList: TrialView[]) => {
    const updated = [...trialsList];
    let changed = false;

    await Promise.all(
      updated.map(async (trial, index) => {
        if (trial.sessions.length === 0) {
          try {
            const sessionsResponse = await apiService.listSessions(trial.trial_id);
            if (sessionsResponse.sessions && sessionsResponse.sessions.length > 0) {
              updated[index] = {
                ...trial,
                sessions: sessionsResponse.sessions.map((s: any) => ({
                  session_id: s.session_id,
                  name: s.name || s.session_id,
                  platforms: s.platforms || [s.platform].filter(Boolean),
                  created_at: s.created_at,
                  status: s.status || 'draft',
                  source_prs_version: s.source_prs_version,
                  target_prs_version: s.target_prs_version,
                })),
              };
              changed = true;
            }
          } catch (err) {
            // Silent fail - sessions not critical for initial display
            // Could not load sessions - continue without session data
          }
        }
      })
    );

    if (changed) {
      setTrials(updated);
    }
  };

  useEffect(() => {
    loadTrials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  /** When Add Session dialog opens, fetch full trial detail so dialog has prs_versions with is_base */
  useEffect(() => {
    if (!addSessionDialogOpen || !selectedTrialForSession) {
      setSessionDialogTrialDetail(null);
      setSessionDialogLoading(false);
      return;
    }
    let cancelled = false;
    setSessionDialogTrialDetail(null);
    setSessionDialogLoading(true);
    apiService
      .getTrial(selectedTrialForSession.trial_id)
      .then((response) => {
        if (cancelled || !response.success) return;
        setSessionDialogTrialDetail(trialResponseToTrialView(response));
      })
      .catch(() => {
        if (!cancelled) setSessionDialogTrialDetail(null);
      })
      .finally(() => {
        if (!cancelled) setSessionDialogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [addSessionDialogOpen, selectedTrialForSession, trialResponseToTrialView]);

  // ============================================
  // ACTIONS
  // ============================================

  const toggleRow = (trialId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(trialId)) {
        newSet.delete(trialId);
      } else {
        newSet.add(trialId);
      }
      return newSet;
    });
  };

  const expandAll = () => setExpandedRows(new Set(trials.map((t) => t.trial_id)));
  const collapseAll = () => setExpandedRows(new Set());

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
    loadTrials({ page: newPage });
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    loadTrials({ page: 0, rowsPerPage: newRowsPerPage });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const handleAddTrial = () => loadTrials();

  const getSessionStatusIcon = (status: string) => {
    switch (status) {
      case 'in_progress':
        return <InProgressIcon fontSize="small" sx={{color: 'warning.main'}} />;
      case 'completed':
        return <CompletedIcon fontSize="small" color="success" />;
      case 'draft':
      default:
        return <DraftIcon fontSize="small" color="disabled" />;
    }
  };

  /** Check if a platform has analysis available (from API flag or live check) */
  const isPlatformAnalysisAvailable = (trialId: string, platform: string, platformInfos: PlatformInfo[]): boolean => {
    const statusInfo = analysisStatus.getStatus(trialId, platform);
    // When we have a retrieved status, trust it: only show checkmark when COMPLETED.
    // This ensures the checkmark is removed when status is NOT_FOUND/FAILED after filter/page change.
    if (statusInfo !== undefined) {
      return statusInfo.status === 'COMPLETED';
    }
    // No live status yet: fall back to API flag
    const info = platformInfos.find((p) => p.platform === platform);
    return info?.analysis_available || false;
  };

  /** Check if a platform's analysis is in progress */
  const isPlatformAnalysisInProgress = (trialId: string, platform: string): boolean => {
    return analysisStatus.isInProgress(trialId, platform);
  };

  /** Can create session: needs at least one platform with analysis and base + target (prs_count >= 2) */
  const canCreateSession = (trial: TrialView): boolean => {
    const hasAnalysis = trial.platforms.some(
      (p) => isPlatformAnalysisAvailable(trial.trial_id, p, trial.platformInfos)
    );
    return trial.platforms.length > 0 && trial.prs_count >= 2 && hasAnalysis;
  };

  const getCannotCreateReason = (trial: TrialView): string => {
    if (trial.platforms.length === 0) return 'Upload at least one protocol (APP or WEB)';
    if (trial.prs_count < 2) return 'Upload at least 2 PRSs (base + target)';
    const hasAnalysis = trial.platforms.some(
      (p) => isPlatformAnalysisAvailable(trial.trial_id, p, trial.platformInfos)
    );
    if (!hasAnalysis) return 'Generate analysis for at least one platform first';
    return '';
  };

  /** Handle analysis trigger/re-trigger for a platform badge */
  const handleTriggerAnalysis = async (trialId: string, platform: string) => {
    await analysisStatus.triggerAnalysis(trialId, platform);
  };

  /**
   * Pre-check workflow mode before navigating to Wizard (like Flutter's trial_row.dart).
   *
   * Cases:
   * 1. can_use_wizard = true -> Navigate directly
   * 2. can_use_wizard = false + can_clear_plans = true -> Show "Clear Plans" dialog
   * 3. can_use_wizard = false + can_clear_plans = false -> Show "Hard Block" dialog
   */
  const handleWizardClick = async (
    trialId: string,
    sessionId: string,
    version: string,
    platform: string,
    platforms: string[]
  ) => {
    const loadingKey = `${trialId}:${sessionId}:${platform}`;
    setWorkflowCheckLoadingKey(loadingKey);
    const navState = { platforms, sessionId };

    try {
      const workflowMode = await apiService.getWorkflowMode(
        trialId,
        sessionId,
        platform as 'app' | 'web'
      );

      if (workflowMode.can_use_wizard) {
        // Case 1: Allowed - sessionId is in URL path for bookmarking/direct access
        navigate(`/wizard/${trialId}/${version}/${platform}/${encodeURIComponent(sessionId)}/req`, { state: navState });
      } else if (workflowMode.can_clear_plans) {
        // Case 2: Show "Clear Plans" dialog
        setPendingWizardNav({trialId, sessionId, version, platform, platforms});
        setWorkflowDialogType('clear_plans');
        setWorkflowDialogReason(
          workflowMode.wizard_disabled_reason || 'Wizard requires clean requirements.'
        );
        setWorkflowDialogSummary({
          planned: workflowMode.requirements_summary?.planned || 0,
        });
        setWorkflowDialogOpen(true);
      } else {
        // Case 3: Hard block
        setWorkflowDialogType('hard_block');
        setWorkflowDialogReason(
          workflowMode.wizard_disabled_reason || 'Wizard cannot be used at this time.'
        );
        setWorkflowDialogSummary({
          active: workflowMode.requirements_summary?.active || 0,
          committed: workflowMode.requirements_summary?.committed || 0,
        });
        setPendingWizardNav({trialId, sessionId, version, platform, platforms});
        setWorkflowDialogOpen(true);
      }
    } catch (err) {
      // Fail open - navigate anyway (like React wizard page's existing behavior)
      navigate(`/wizard/${trialId}/${version}/${platform}/${encodeURIComponent(sessionId)}/req`, { state: navState });
    } finally {
      setWorkflowCheckLoadingKey(null);
    }
  };

  /** Clear plans and navigate to wizard */
  const handleClearPlansAndNavigate = async () => {
    if (!pendingWizardNav) return;

    try {
      await apiService.clearAllPlans(
        pendingWizardNav.trialId,
        pendingWizardNav.sessionId,
        pendingWizardNav.platform as 'app' | 'web'
      );
      setWorkflowDialogOpen(false);
      navigate(`/wizard/${pendingWizardNav.trialId}/${pendingWizardNav.version}/${pendingWizardNav.platform}/${encodeURIComponent(pendingWizardNav.sessionId)}/req`, {
        state: {
          platforms: pendingWizardNav.platforms,
          sessionId: pendingWizardNav.sessionId,
        },
      });
    } catch (err: any) {
      setError(`Failed to clear plans: ${err?.message || 'Unknown error'}`);
      setWorkflowDialogOpen(false);
    }
  };

  /** Open edit description dialog */
  const handleEditDescription = (trial: TrialView) => {
    setEditingTrial(trial);
    setEditedDescription(trial.description || '');
    setEditDescriptionDialogOpen(true);
  };

  /** Save edited description */
  const handleSaveDescription = async () => {
    if (!editingTrial) return;

    try {
      setSavingDescription(true);
      await apiService.updateTrialDescription(editingTrial.trial_id, editedDescription);
      
      // Update local state
      setTrials(trials.map(t => 
        t.trial_id === editingTrial.trial_id 
          ? { ...t, description: editedDescription }
          : t
      ));
      
      setEditDescriptionDialogOpen(false);
      setEditingTrial(null);
      setEditedDescription('');
    } catch (err: any) {
      setError(`Failed to update description: ${err?.message || 'Unknown error'}`);
    } finally {
      setSavingDescription(false);
    }
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <Container maxWidth={false} sx={{mt: 4, mb: 4, px: 3}}>
      {/* Loading overlay */}
      <ProgressOverlay inProgress={loading} message="Loading trials..." fullOverlay />

      {/* Header */}
      <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3}}>
        <Typography variant="h4" component="h1" gutterBottom sx={{fontWeight: 400}}>
          Trials
        </Typography>
        <Box sx={{display: 'flex', gap: 1}}>
          <Tooltip title="Refresh list">
            <IconButton onClick={() => loadTrials()} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="outlined" startIcon={<KBIcon />} onClick={() => navigate('/admin/kb')}>
            KB Admin
          </Button>
          <Button variant="outlined" onClick={() => setAddTrialDialogOpen(true)}>
            + ADD TRIAL
          </Button>
        </Box>
      </Box>

      {/* Filter + Expand/Collapse All */}
      <Box sx={{mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
        <TextField
          size="small"
          placeholder="Filter trials..."
          value={filterText}
          onChange={(e) => {
            const value = e.target.value;
            setFilterText(value);

            if (debounceTimerRef.current) {
              clearTimeout(debounceTimerRef.current);
            }
            debounceTimerRef.current = setTimeout(() => {
              setPage(0);
              loadTrials({ page: 0, filter: value });
            }, 300);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
              }
              setPage(0);
              loadTrials({ page: 0, filter: filterText });
            }
          }}
          sx={{width: 300}}
          InputProps={{
            startAdornment: <SearchIcon sx={{mr: 1, color: 'action.disabled'}} />,
          }}
        />

        {trials.length > 0 && (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ExpandAllIcon />}
              onClick={expandAll}
              disabled={trials.every((t) => expandedRows.has(t.trial_id))}
            >
              Expand All
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CollapseAllIcon />}
              onClick={collapseAll}
              disabled={trials.every((t) => !expandedRows.has(t.trial_id))}
            >
              Collapse All
            </Button>
          </Stack>
        )}
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{mb: 2}} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Empty State */}
      {!loading && trials.length === 0 && (
        <Paper sx={{p: 4, textAlign: 'center'}}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No trials found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click "+ ADD TRIAL" to get started
          </Typography>
        </Paper>
      )}

      {/* Trials Table */}
      {!loading && trials.length > 0 && (
        <TableContainer
          component={Paper}
          elevation={0}
          variant="outlined"
          sx={{
            borderRadius: 1,
            overflow: 'hidden',
            overflowX: 'auto',
            scrollbarGutter: 'stable',
            '&::-webkit-scrollbar': { height: 12 },
            '&::-webkit-scrollbar-track': { bgcolor: 'grey.200' },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.400', borderRadius: 6 },
            '&::-webkit-scrollbar-thumb:hover': { bgcolor: 'grey.500' },
          }}
        >
          <Table sx={{minWidth: 800}}>
            <TableHead>
              <TableRow sx={{bgcolor: 'primary.main', '& .MuiTableCell-root': {borderBottom: 'none', position: 'relative'}, '& .MuiTableCell-root:nth-of-type(n+3)::before': {content: '""', position: 'absolute', left: 0, top: '15%', bottom: '15%', borderLeft: '2px solid rgba(255,255,255,0.5)', pointerEvents: 'none'}}}>
                <TableCell width="50px" sx={{color: 'common.white'}} />
                <TableCell sx={{color: 'common.white', fontSize: '16px', width: '15%'}}>Trial ID</TableCell>
                <TableCell sx={{color: 'common.white', fontSize: '16px', width: '40%'}}>Description</TableCell>
                <TableCell sx={{color: 'common.white', fontSize: '16px', width: '15%'}}>Protocols</TableCell>
                <TableCell sx={{color: 'common.white', fontSize: '16px', width: '15%'}}>PRSs</TableCell>
                <TableCell sx={{color: 'common.white', fontSize: '16px', width: '15%'}}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {trials.map((trial) => {
                const isExpanded = expandedRows.has(trial.trial_id);
                // Force re-render when analysis statuses change
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const _statusesTrigger = analysisStatus.statuses;

                return (
                  <React.Fragment key={trial.trial_id}>
                    {/* ---- Trial Row ---- */}
                    <TableRow
                      hover
                      onClick={() => toggleRow(trial.trial_id)}
                      sx={{
                        cursor: 'pointer',
                        '& > *': {borderBottom: isExpanded ? 'none' : undefined},
                      }}
                    >
                      {/* Expand toggle */}
                      <TableCell>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleRow(trial.trial_id); }}>
                          {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
                        </IconButton>
                      </TableCell>

                      {/* Trial ID */}
                      <TableCell>
                        <Typography variant="body1" fontWeight="medium">
                          {trial.trial_id}
                        </Typography>
                      </TableCell>

                      {/* Description */}
                      <TableCell>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                          <Typography
                            variant="body1"
                            sx={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1}}
                            title={trial.description || ''}
                          >
                            {trial.description || '-'}
                          </Typography>
                          {isExpanded && (
                            <Tooltip title="Edit description">
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleEditDescription(trial); }}
                                sx={{color: 'primary.main'}}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>

                      {/* Protocols (platform badges with analysis status indicators) */}
                      <TableCell>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            border: '1px solid',
                            borderColor: 'grey.300',
                            borderRadius: 2,
                            px: 1.5,
                            py: 0.75,
                            bgcolor: 'grey.50',
                          }}
                        >
                          {trial.platforms.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          ) : (
                            trial.platforms.map((p) => {
                              const isAvailable = isPlatformAnalysisAvailable(trial.trial_id, p, trial.platformInfos);
                              const isRunning = isPlatformAnalysisInProgress(trial.trial_id, p);
                              const statusInfo = analysisStatus.getStatus(trial.trial_id, p);

                              return (
                                <Tooltip
                                  key={p}
                                  title={
                                    isRunning
                                      ? `Analysis in progress (${statusInfo?.progressPercent ?? 0}%) - Click to view`
                                      : isAvailable
                                        ? `${p.toUpperCase()} analysis ready - Click to view details`
                                        : `${p.toUpperCase()} - Click to start analysis`
                                  }
                                >
                                  <Chip
                                    label={
                                      <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                        {isRunning ? (
                                          <CircularProgress 
                                            size={12} 
                                            thickness={5} 
                                            sx={{
                                              color: p === 'web' ? 'secondary.dark' : 'primary.dark',
                                            }} 
                                          />
                                        ) : isAvailable ? (
                                          <CompletedIcon sx={{fontSize: 14}} />
                                        ) : null}
                                        {p.toUpperCase()}
                                      </Box>
                                    }
                                    size="small"
                                    variant={isAvailable ? 'filled' : 'outlined'}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // Always open the dialog to view status
                                      setAnalysisProgressTrialId(trial.trial_id);
                                      setAnalysisProgressPlatform(p);
                                      setAnalysisProgressOpen(true);
                                    }}
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: '0.7rem',
                                      cursor: 'pointer',
                                      height: 26,
                                      ...(isAvailable
                                        ? {
                                            bgcolor: p === 'app' ? '#277EA1' : '#2e7d32',
                                            color: '#fff',
                                            '& .MuiChip-label': { color: '#fff' },
                                            '&:hover': { bgcolor: p === 'app' ? '#005272' : '#1b5e20' },
                                          }
                                        : {
                                            borderColor: p === 'app' ? '#277EA1' : '#2e7d32',
                                            color: p === 'app' ? '#277EA1' : '#2e7d32',
                                            '&:hover': { bgcolor: p === 'app' ? 'rgba(39, 126, 161, 0.08)' : 'rgba(46, 125, 50, 0.08)' },
                                          }),
                                    }}
                                  />
                                </Tooltip>
                              );
                            })
                          )}
                          <Tooltip title="Manage Protocols">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTrialForProtocol(trial);
                                setUploadProtocolDialogOpen(true);
                              }}
                              sx={{ color: '#277EA1', p: 0.25 }}
                            >
                              <SettingsIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>

                      {/* PRSs (count with settings gear) */}
                      <TableCell>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.75,
                            border: '1px solid',
                            borderColor: 'grey.300',
                            borderRadius: 2,
                            px: 1.25,
                            py: 0.5,
                            bgcolor: 'grey.50',
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                              minWidth: 10,
                              textAlign: 'center',
                              color: 'grey.800',
                            }}
                          >
                            {trial.prs_count ?? 0}
                          </Typography>
                          <Tooltip title="Manage PRSs">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTrialForPRS({
                                  trialId: trial.trial_id,
                                  platform: (trial.platforms[0] || 'app') as 'app' | 'web',
                                });
                                setUploadPRSDialogOpen(true);
                              }}
                              disabled={trial.platforms.length === 0}
                              sx={{ color: '#277EA1', p: 0.25 }}
                            >
                              <SettingsIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>

                      {/* + Session button */}
                      <TableCell>
                        <Tooltip
                          title={
                            canCreateSession(trial)
                              ? 'Create a new session'
                              : getCannotCreateReason(trial)
                          }
                        >
                          <span>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTrialForSession(trial);
                                setAddSessionDialogOpen(true);
                              }}
                              disabled={!canCreateSession(trial)}
                              sx={{
                                textTransform: 'none',
                              }}
                            >
                              Add Session
                            </Button>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>

                    {/* ---- Expanded Sessions Section ---- */}
                    <TableRow>
                      <TableCell style={{paddingBottom: 0, paddingTop: 0}} colSpan={7}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ml: 6, mb: 1}}>
                            {/* Sessions header */}
                            <Box
                              sx={{
                                py: 1,
                                px: 2,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{fontWeight: 600, color: 'primary.main'}}
                              >
                                Sessions
                              </Typography>
                            </Box>

                            {/* Sessions list */}
                            {trial.sessions.length === 0 ? (
                              <Box sx={{py: 2, px: 2, display: 'flex', alignItems: 'center', gap: 1}}>
                                <DraftIcon fontSize="small" color="disabled" />
                                <Typography variant="body2" color="text.secondary">
                                  No sessions yet. Click "+ Session" to create one.
                                </Typography>
                              </Box>
                            ) : (
                              trial.sessions.map((session, index) => (
                                <Box
                                  key={session.session_id}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 2,
                                    py: 1.5,
                                    borderBottom:
                                      index < trial.sessions.length - 1
                                        ? '1px solid'
                                        : 'none',
                                    borderColor: 'divider',
                                    '&:hover': {bgcolor: 'action.hover'},
                                  }}
                                >
                                  {/* Status icon */}
                                  {getSessionStatusIcon(session.status)}

                                  {/* Session name */}
                                  <Typography
                                    variant="body2"
                                    sx={{ml: 1.5, fontWeight: 500, flex: 2}}
                                  >
                                    {session.name || session.session_id}
                                  </Typography>

                                  {/* Created date */}
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{width: 160}}
                                  >
                                    {formatDate(session.created_at)}
                                  </Typography>

                                  {/* Platform action groups */}
                                  <Box sx={{display: 'flex', gap: 1, ml: 'auto'}}>
                                    {(session.platforms.length > 0
                                      ? session.platforms
                                      : trial.platforms
                                    ).map((platform) => {
                                      const thisKey = `${trial.trial_id}:${session.session_id}:${platform}`;
                                      return (
                                        <PlatformActionChip
                                          key={platform}
                                          platform={platform}
                                          loading={workflowCheckLoadingKey === thisKey}
                                          onWizardClick={() => {
                                            const targetVer =
                                              session.target_prs_version ||
                                              trial.prs_versions.find(
                                                (v) => !v.is_base
                                              )?.version;
                                            const version = targetVer || session.session_id;
                                            handleWizardClick(
                                              trial.trial_id,
                                              session.session_id,
                                              version,
                                              platform,
                                              trial.platforms
                                            );
                                          }}
                                        />
                                      );
                                    })}
                                  </Box>
                                </Box>
                              ))
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 50]}
          />
        </TableContainer>
      )}

      {/* Trials count */}
      {!loading && trials.length > 0 && (
        <Box sx={{mt: 2, textAlign: 'center'}}>
          <Typography variant="body2" color="text.secondary">
            Total: {totalCount} trial{totalCount !== 1 ? 's' : ''}
          </Typography>
        </Box>
      )}

      {/* ---- DIALOGS ---- */}

      {/* Add Trial Dialog */}
      <AddTrialDialog
        open={addTrialDialogOpen}
        onClose={() => setAddTrialDialogOpen(false)}
        onAdd={handleAddTrial}
        existingTrialIds={trials.map((t) => t.trial_id)}
      />

      {/* Manage Protocol Dialog (with auto-analysis trigger like Flutter) */}
      {selectedTrialForProtocol && (
        <ManageProtocolDialog
          open={uploadProtocolDialogOpen}
          onClose={() => {
            setUploadProtocolDialogOpen(false);
            setSelectedTrialForProtocol(null);
          }}
          onSuccess={(uploadedPlatform?: string) => {
            if (selectedTrialForProtocol && (uploadedPlatform === 'app' || uploadedPlatform === 'web')) {
              // Refresh only this trial and set its status to loading, then checkStatus for real status
              analysisStatus.triggerAnalysis(selectedTrialForProtocol.trial_id, uploadedPlatform);
              refreshTrialAndStatus(selectedTrialForProtocol.trial_id, uploadedPlatform);
            }
          }}
          trialId={selectedTrialForProtocol.trial_id}
          existingPlatforms={(selectedTrialForProtocol.platforms || []) as ('app' | 'web')[]}
          appFileName={selectedTrialForProtocol.app_zip_filename}
          webFileName={selectedTrialForProtocol.web_zip_filename}
        />
      )}

      {/* Manage PRS Dialog */}
      {selectedTrialForPRS && (
        <ManagePRSDialog
          open={uploadPRSDialogOpen}
          onClose={() => {
            setUploadPRSDialogOpen(false);
            setSelectedTrialForPRS(null);
          }}
          onSuccess={() => loadTrials()}
          trialId={selectedTrialForPRS.trialId}
        />
      )}

      {/* Add Session Dialog: fetches full trial on open for prs_versions (base/target) */}
      {selectedTrialForSession && (
        <AddSessionDialog
          open={addSessionDialogOpen}
          onClose={() => {
            setAddSessionDialogOpen(false);
            setSelectedTrialForSession(null);
            setSessionDialogTrialDetail(null);
          }}
          onSuccess={() => loadTrials()}
          trialId={selectedTrialForSession.trial_id}
          loading={sessionDialogLoading}
          platforms={(sessionDialogTrialDetail ?? selectedTrialForSession).platforms}
          prsVersions={(sessionDialogTrialDetail ?? selectedTrialForSession).prs_versions}
          existingSessionIds={(sessionDialogTrialDetail ?? selectedTrialForSession).sessions.map((s) => s.session_id)}
          isAnalysisAvailable={(platform: string) =>
            isPlatformAnalysisAvailable(
              selectedTrialForSession.trial_id,
              platform,
              (sessionDialogTrialDetail ?? selectedTrialForSession).platformInfos
            )
          }
        />
      )}

      {/* Workflow Mode Dialog (Clear Plans / Hard Block - like Flutter) */}
      <Dialog
        open={workflowDialogOpen}
        onClose={() => setWorkflowDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <WarningIcon color={workflowDialogType === 'hard_block' ? 'error' : 'warning'} />
          {workflowDialogType === 'hard_block' ? 'Wizard Unavailable' : 'Clear Plans Required'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{mb: 2}}>{workflowDialogReason}</Typography>

          {workflowDialogType === 'clear_plans' && (
            <Alert severity="warning" sx={{mb: 2}}>
              {workflowDialogSummary.planned || 0} planned requirement(s) will be reset to pending.
              All plan actions will be deleted. You can regenerate the plan in Wizard.
            </Alert>
          )}

          {workflowDialogType === 'hard_block' && (
            <>
              {(workflowDialogSummary.active || 0) > 0 && (
                <Box sx={{mb: 2}}>
                  <Typography variant="subtitle2" fontWeight={600}>To use Wizard:</Typography>
                  <Typography variant="body2" sx={{mt: 0.5}}>
                    Open Copilot to discard active executions
                  </Typography>
                </Box>
              )}
              {(workflowDialogSummary.committed || 0) > 0 && (
                <Alert severity="info" sx={{mb: 2}}>
                  {workflowDialogSummary.committed} requirement(s) have been committed.
                  Create a new session if further changes are needed.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWorkflowDialogOpen(false)}>
            {workflowDialogType === 'hard_block' ? 'Close' : 'Cancel'}
          </Button>
          {workflowDialogType === 'clear_plans' && (
            <Button
              variant="contained"
              color="warning"
              onClick={handleClearPlansAndNavigate}
            >
              Clear Plans & Open Wizard
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Description Dialog */}
      <Dialog
        open={editDescriptionDialogOpen}
        onClose={() => {
          setEditDescriptionDialogOpen(false);
          setEditingTrial(null);
          setEditedDescription('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Trial Description</DialogTitle>
        <DialogContent>
          {editingTrial && (
            <Box sx={{pt: 1}}>
              <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                Trial ID: <strong>{editingTrial.trial_id}</strong>
              </Typography>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={4}
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                placeholder="Enter trial description..."
                disabled={savingDescription}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditDescriptionDialogOpen(false);
              setEditingTrial(null);
              setEditedDescription('');
            }}
            disabled={savingDescription}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveDescription}
            disabled={savingDescription}
          >
            {savingDescription ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Analysis Progress Dialog (like Flutter's analysis status view) */}
      <Dialog
        open={analysisProgressOpen}
        onClose={() => {
          setAnalysisProgressOpen(false);
          setShowFailedWorkerDetails(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Analysis Status - {analysisProgressPlatform.toUpperCase()}
        </DialogTitle>
        <DialogContent>
          {(() => {
            const statusInfo = analysisStatus.getStatus(analysisProgressTrialId, analysisProgressPlatform);
            if (!statusInfo) {
              return (
                <Box sx={{py: 2}}>
                  <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                    No analysis found for this platform.
                  </Typography>
                  <Alert severity="info">
                    Click "Start Analysis" below to generate analysis for {analysisProgressPlatform.toUpperCase()}.
                  </Alert>
                </Box>
              );
            }

            return (
              <Box sx={{py: 2}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 2}}>
                  <Chip
                    label={statusInfo.status}
                    size="small"
                    color={
                      statusInfo.status === 'COMPLETED' ? 'success'
                        : statusInfo.status === 'FAILED' ? 'error'
                          : statusInfo.status === 'RUNNING' ? 'warning'
                            : 'default'
                    }
                  />
                  <Typography variant="body2" color="text.secondary">
                    {analysisProgressTrialId}
                  </Typography>
                </Box>

                {(statusInfo.status === 'RUNNING' || statusInfo.status === 'QUEUED') && (
                  <Box sx={{mb: 2}}>
                    <LinearProgress
                      variant={statusInfo.progressPercent > 0 ? 'determinate' : 'indeterminate'}
                      value={statusInfo.progressPercent}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{mt: 0.5, display: 'block'}}>
                      {statusInfo.progressPercent > 0 ? `${statusInfo.progressPercent}%` : 'Starting...'}
                    </Typography>
                  </Box>
                )}

                {statusInfo.workers && statusInfo.workers.total !== 0 && (
                  <Box sx={{mt: 1}}>
                    <Typography variant="subtitle2" sx={{mb: 1}}>Workers</Typography>
                    <Box sx={{display: 'flex', gap: 2}}>
                      <Typography variant="body2">
                        Total: <strong>{statusInfo.workers.total}</strong>
                      </Typography>
                      <Typography variant="body2" color="success.main">
                        Done: <strong>{statusInfo.workers.completed}</strong>
                      </Typography>
                      <Typography variant="body2" color="warning.main">
                        Running: <strong>{statusInfo.workers.running}</strong>
                      </Typography>
                      {statusInfo.workers.failed > 0 && (
                        <Typography variant="body2" color="error.main">
                          Failed: <strong>{statusInfo.workers.failed}</strong>
                        </Typography>
                      )}
                    </Box>

                    {/* Show Details for Failed Workers */}
                    {statusInfo.workers.failed > 0 && statusInfo.workerDetails && (
                      <Box sx={{mt: 2}}>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => setShowFailedWorkerDetails(!showFailedWorkerDetails)}
                          sx={{textTransform: 'none', color: 'error.main'}}
                        >
                          {showFailedWorkerDetails ? 'Hide Details' : 'Show Details'}
                        </Button>
                        
                        <Collapse in={showFailedWorkerDetails}>
                          <Box sx={{mt: 2, maxHeight: 300, overflowY: 'auto'}}>
                            {statusInfo.workerDetails
                              .filter((w) => w.status === 'failed' || w.status === 'timeout')
                              .map((worker, index) => (
                                <Paper
                                  key={index}
                                  elevation={0}
                                  sx={{
                                    p: 2,
                                    mb: 1,
                                    border: '1px solid',
                                    borderColor: 'error.light',
                                    bgcolor: 'error.lighter',
                                  }}
                                >
                                  <Typography variant="body2" fontWeight="medium" color="error.dark">
                                    {worker.chunk_id || worker.worker_id || `Worker ${index + 1}`}
                                  </Typography>
                                  {worker.error && (
                                    <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 0.5}}>
                                      {worker.error}
                                    </Typography>
                                  )}
                                  {worker.status === 'timeout' && !worker.error && (
                                    <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 0.5}}>
                                      Worker timed out
                                    </Typography>
                                  )}
                                </Paper>
                              ))}
                          </Box>
                        </Collapse>
                      </Box>
                    )}
                  </Box>
                )}

                {statusInfo.errorMessage && (
                  <Alert severity="error" sx={{mt: 2}}>
                    {statusInfo.errorMessage}
                  </Alert>
                )}

                {statusInfo.status === 'COMPLETED' && (
                  <Alert severity="success" sx={{mt: 2}}>
                    Analysis completed successfully! You can regenerate it if needed.
                  </Alert>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAnalysisProgressOpen(false);
            setShowFailedWorkerDetails(false);
          }}>Close</Button>
          {(() => {
            const statusInfo = analysisStatus.getStatus(analysisProgressTrialId, analysisProgressPlatform);
            const isInProgress = statusInfo?.status === 'RUNNING' || statusInfo?.status === 'QUEUED';
            const isCompleted = statusInfo?.status === 'COMPLETED';
            
            return (
              <Button
                variant="contained"
                color={isCompleted ? 'warning' : 'primary'}
                onClick={async () => {
                  await handleTriggerAnalysis(analysisProgressTrialId, analysisProgressPlatform);
                }}
                disabled={isInProgress}
              >
                {isCompleted ? 'Regenerate Analysis' : isInProgress ? 'Analysis Running...' : 'Start Analysis'}
              </Button>
            );
          })()}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProtocolList;
